import React, { useState, useEffect } from 'react';
import { X, Loader2, Wand2, Copy, FileText, AlertCircle, ExternalLink, Download, Save } from 'lucide-react';
import { generateTaskDescription } from '../lib/openai';
import { supabase } from '../lib/supabase';
import { toast } from '../lib/toast';
import { jsPDF } from 'jspdf';
import * as pdfjsLib from 'pdfjs-dist';

// Import the worker as a URL
import pdfWorker from 'pdfjs-dist/build/pdf.worker?url';

// Set the worker source
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

interface AIAgentInteractionProps {
  agent: {
    id?: string;
    name: string;
    description: string;
    instructions?: string;
    attachment_url?: string;
    attachment_name?: string;
    agent_type?: string;
  };
  onClose: () => void;
}

// Retry configuration
const RETRY_ATTEMPTS = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 5000; // 5 seconds
const FETCH_TIMEOUT = 30000; // 30 seconds

// Helper function to implement fetch with timeout
const fetchWithTimeout = async (url: string, options: RequestInit = {}, timeout = FETCH_TIMEOUT) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    throw error;
  }
};

// Helper function to implement retry logic with exponential backoff
const fetchWithRetry = async (url: string, options: RequestInit = {}) => {
  let lastError: Error | null = null;
  let delay = INITIAL_RETRY_DELAY;

  for (let attempt = 1; attempt <= RETRY_ATTEMPTS; attempt++) {
    try {
      const response = await fetchWithTimeout(url, options);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response;
    } catch (error) {
      lastError = error as Error;
      if (error.name === 'AbortError') {
        throw new Error('Request timed out');
      }
      
      // Don't wait on the last attempt
      if (attempt < RETRY_ATTEMPTS) {
        await new Promise(resolve => setTimeout(resolve, delay));
        delay = Math.min(delay * 2, MAX_RETRY_DELAY); // Exponential backoff
      }
    }
  }

  throw new Error(`Failed after ${RETRY_ATTEMPTS} attempts. Last error: ${lastError?.message}`);
};

export default function AIAgentInteraction({ agent, onClose }: AIAgentInteractionProps) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [documentContent, setDocumentContent] = useState<string | null>(null);
  const [isProcessingDocument, setIsProcessingDocument] = useState(false);
  const [selectedModel, setSelectedModel] = useState('gpt-3.5-turbo');
  const [savedOutput, setSavedOutput] = useState<{ id: string; saved: boolean } | null>(null);
  const [savingOutput, setSavingOutput] = useState(false);

  useEffect(() => {
    if (agent.attachment_url) {
      fetchAndProcessDocument();
    }
  }, [agent.attachment_url]);

  const fetchAndProcessDocument = async () => {
    if (!agent?.attachment_url) return;

    const maxCharsPerPage = 50000;
    setIsProcessingDocument(true);
    let text = '';

    try {
      const response = await fetchWithRetry(agent.attachment_url);
      const blob = await response.blob();
      const fileName = agent.attachment_name?.toLowerCase() || '';

      if (fileName.endsWith('.pdf')) {
        const arrayBuffer = await blob.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

        const maxPages = pdf.numPages;
        const textContent = [];

        for (let i = 1; i <= maxPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          let pageText = '';
          let lastY = null;
          
          content.items.forEach((item: any) => {
            if (lastY !== null && lastY !== item.transform[5]) {
              pageText += '\n';
            }
            pageText += item.str + ' ';
            lastY = item.transform[5];
          });
          
          if (pageText.length > maxCharsPerPage) {
            pageText = pageText.substring(0, maxCharsPerPage);
          }
          
          textContent.push(pageText);
        }
        text = textContent.join('\n\n').trim();
      } else if (agent.attachment_name?.toLowerCase().endsWith('.docx')) {
        const mammoth = await import('mammoth');
        const result = await mammoth.extractRawText({ arrayBuffer: await blob.arrayBuffer() });
        text = result.value;
      } else {
        text = await blob.text();
      }
      
      if (!text.trim()) {
        throw new Error('No text content found in document');
      }

      setDocumentContent(text);
      toast.success('Document processed successfully');
    } catch (error) {
      console.error('Error processing document:', error);
      const message = error instanceof Error ? error.message : 'Failed to process document';
      toast.error(message);
      setDocumentContent(null);
    } finally {
      setIsProcessingDocument(false);
    }
  };

  const findRelevantContext = (query: string, content: string): string | null => {
    if (!content) return null;

    try {
      const sections = content.split(/(?:\r?\n){2,}/).filter(section => section.trim());
      
      const keywords = query.toLowerCase()
        .replace(/[^\w\s]/g, '')
        .split(/\s+/)
        .filter(word => 
          word.length > 2 && 
          !['what', 'when', 'where', 'how', 'why', 'who', 'the', 'and', 'that', 'this', 'for', 'are', 'was', 'will', 'can', 'should'].includes(word)
        );

      if (keywords.length === 0) {
        return content.substring(0, 1500);
      }

      const scoredSections = sections.map(section => {
        const sectionLower = section.toLowerCase();
        let score = 0;
        
        keywords.forEach(keyword => {
          const exactMatches = (sectionLower.match(new RegExp(`\\b${keyword}\\b`, 'g')) || []).length;
          score += exactMatches * 3;
          
          if (keyword.length > 4) {
            const partialMatches = (sectionLower.match(new RegExp(keyword.slice(0, -1), 'g')) || []).length;
            score += partialMatches;
          }
          
          if (exactMatches > 1) {
            const words = sectionLower.split(/\s+/);
            let minDistance = words.length;
            for (let i = 0; i < words.length; i++) {
              for (let j = i + 1; j < words.length; j++) {
                if (words[i] === keyword && words[j] === keyword) {
                  minDistance = Math.min(minDistance, j - i);
                }
              }
            }
            score += (10 - minDistance) * 0.1;
          }
        });

        return { section, score };
      });

      const relevantSections = scoredSections
        .filter(s => s.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 3)
        .map(s => s.section);

      if (relevantSections.length === 0) return null;

      return relevantSections.join('\n\n').substring(0, 2000);
    } catch (error) {
      console.error('Error finding relevant context:', error);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) {
      toast.error('Please enter your request');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // Text generation logic
      const relevantContext = documentContent ? findRelevantContext(input, documentContent) : null;
      const contextPrompt = `
${agent?.system_prompt || ''}

${agent?.instructions ? `Instructions:\n${agent?.instructions}\n` : ''}

${relevantContext ? `Document Context:\n---\n${relevantContext}\n---\n` : ''}

User Question: ${input}

Response Guidelines:
1. If the document contains relevant information:
   - Use it as your primary source
   - Quote specific parts when appropriate
   - Clearly indicate which information comes from the document
2. If the document information is incomplete:
   - Start with document information
   - Clearly separate document-based and general knowledge
   - Use phrases like "According to the document..." and "Additionally..."
3. If no relevant document information is found:
   - Explicitly state that the document doesn't contain relevant information
   - Provide a general response based on your knowledge
4. Keep responses concise and focused
5. Use formatting to improve readability
      `;

        const response = await generateTaskDescription(contextPrompt, 'task', 'detailed', 'openai', selectedModel);
        
        const finalResponse = (agent.attachment_url && !relevantContext) ? 
          `<div class="bg-yellow-50 dark:bg-yellow-900/50 p-4 rounded-lg mb-4">
            <div class="flex items-center">
              <AlertCircle class="h-5 w-5 text-yellow-400 mr-2" />
              <p class="text-sm text-yellow-700 dark:text-yellow-200">
                Note: This response is not based on the provided documents as no relevant information was found.
              </p>
            </div>
          </div>${response}` : response;

        setResult(finalResponse);
        
      setSavedOutput({
        id: crypto.randomUUID(),
        saved: false
      });
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error('Failed to generate response');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOutput = async () => {
    if (!result || !savedOutput || savedOutput.saved) return;
    
    setSavingOutput(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to save outputs');
        setSavingOutput(false);
        return;
      }

      let agentId: string | undefined = agent.id;

      if (!agentId) {
        const { data: existingAgent, error: fetchError } = await supabase
          .from('ai_agents')
          .select('id')
          .eq('name', agent.name)
          .limit(1)
          .maybeSingle();
        
        if (fetchError && fetchError.code !== 'PGRST116') {
          console.error('Error fetching agent:', fetchError);
          toast.error('Failed to save output');
          setSavingOutput(false);
          return;
        }
        
        if (existingAgent) {
          agentId = existingAgent.id;
        } else {
          const { data: newAgent, error: createError } = await supabase
            .from('ai_agents')
            .insert({
              name: agent.name,
              goals: agent.description,
              system_prompt: agent.system_prompt || agent.description,
              is_custom: true,
              user_id: user.id,
              agent_type: 'text'
            })
            .select('id')
            .single();
            
          if (createError) {
            console.error('Error creating temporary agent:', createError);
            toast.error('Failed to save output');
            setSavingOutput(false);
            return;
          }
          
          agentId = newAgent.id;
        }
      }
      
      if (agentId) {
        const outputContent = result;
        if (!outputContent) {
          toast.error('No output to save');
          setSavingOutput(false);
          return;
        }
        
        const { error: insertError } = await supabase.from('ai_agent_outputs').insert({
          user_id: user.id,
          agent_id: agentId,
          prompt: input,
          output_url: outputContent,
          generation_type: 'text',
          llm_model: selectedModel,
          metadata: {},
          agent_type: 'text'
        });

        if (insertError) {
          console.error('Error saving output:', insertError);
          toast.error('Failed to save output');
          setSavingOutput(false);
          return;
        }
        
        toast.success('Output saved successfully');
        setSavedOutput({...savedOutput, saved: true});
      }
    } catch (error) {
      console.error('Error saving agent output:', error);
      toast.error('Failed to save output');
    } finally {
      setSavingOutput(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(result || '');
      toast.success('Copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleDownload = () => {
    try {
      const stripHtml = (html: string) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.textContent || '';
      };

      const content = `${agent.name}\n\nRequest: ${input}\n\nResponse:\n${stripHtml(result || '')}`;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(agent?.name || 'ai-agent').toLowerCase().replace(/\s+/g, '-')}-response.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading response:', error);
      toast.error('Failed to download response');
    }
  };

  const downloadPDF = () => {
    try {
      if (!result) return;
      
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      const lineHeight = 7;
      
      pdf.setFillColor(79, 70, 229);
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      const title = `${agent?.name || 'AI Agent'} Response`;
      const titleWidth = pdf.getStringUnitWidth(title) * 22 / pdf.internal.scaleFactor;
      const titleX = (pageWidth - titleWidth) / 2;
      pdf.text(title, titleX, 25);
      
      pdf.setTextColor(0, 0, 0);
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(result, 'text/html');
      
      let y = 50;
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('User Request:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const wrappedPrompt = pdf.splitTextToSize(input, pageWidth - (margin * 2));
      pdf.text(wrappedPrompt, margin, y);
      y += wrappedPrompt.length * lineHeight + 15;
      
      const alertBox = doc.querySelector('.bg-yellow-50, .bg-yellow-900\\/50');
      if (alertBox) {
        pdf.setFillColor(255, 250, 230);
        pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 20, 'F');
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'italic');
        const alertText = alertBox.textContent?.trim() || 'Note: This response is not based on the provided documents.';
        const wrappedAlert = pdf.splitTextToSize(alertText, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedAlert, margin, y);
        y += wrappedAlert.length * lineHeight + 10;
        
        alertBox.remove();
      }
      
      const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
      
      if (headings.length === 0) {
        const content = doc.body.textContent || '';
        const wrappedContent = pdf.splitTextToSize(content, pageWidth - (margin * 2));
        
        if (y + wrappedContent.length * lineHeight > pdf.internal.pageSize.getHeight() - 20) {
          pdf.addPage();
          y = 20;
        }
        
        pdf.text(wrappedContent, margin, y);
      } else {
        headings.forEach((heading) => {
          if (y > pdf.internal.pageSize.getHeight() - 40) {
            pdf.addPage();
            y = 20;
          }
          
          const level = parseInt(heading.tagName.substring(1));
          pdf.setFontSize(16 - (level - 1) * 2);
          pdf.setFont('helvetica', 'bold');
          
          if (level <= 3) {
            pdf.setFillColor(240, 247, 255);
            pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
          }
          
          pdf.text(heading.textContent || '', margin, y);
          y += 12;
          
          let nextElement = heading.nextElementSibling;
          while (nextElement && !['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(nextElement.tagName)) {
            if (y > pdf.internal.pageSize.getHeight() - 20) {
              pdf.addPage();
              y = 20;
            }
            
            if (nextElement.tagName === 'P') {
              pdf.setFontSize(11);
              pdf.setFont('helvetica', 'normal');
              const wrappedText = pdf.splitTextToSize(nextElement.textContent || '', pageWidth - (margin * 2));
              pdf.text(wrappedText, margin, y);
              y += wrappedText.length * lineHeight;
            } else if (nextElement.tagName === 'UL' || nextElement.tagName === 'OL') {
              const items = nextElement.querySelectorAll('li');
              items.forEach((item, index) => {
                if (y > pdf.internal.pageSize.getHeight() - 20) {
                  pdf.addPage();
                  y = 20;
                }
                
                pdf.setFontSize(11);
                pdf.setFont('helvetica', 'normal');
                
                const prefix = nextElement.tagName === 'OL' ? `${index + 1}.` : '•';
                pdf.text(prefix, margin, y);
                
                const wrappedItem = pdf.splitTextToSize(item.textContent || '', pageWidth - (margin * 2) - 10);
                pdf.text(wrappedItem, margin + 10, y);
                y += wrappedItem.length * lineHeight;
              });
            } else if (nextElement.tagName === 'PRE') {
              pdf.setFontSize(10);
              pdf.setFont('courier', 'normal');
              pdf.setFillColor(245, 247, 250);
              
              const codeText = nextElement.textContent || '';
              const wrappedCode = pdf.splitTextToSize(codeText, pageWidth - (margin * 2) - 10);
              
              pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, wrappedCode.length * lineHeight + 10, 'F');
              
              pdf.text(wrappedCode, margin, y);
              y += wrappedCode.length * lineHeight + 5;
              
              pdf.setFont('helvetica', 'normal');
              pdf.setFontSize(11);
            }
            
            nextElement = nextElement.nextElementSibling;
            y += 3;
          }
          
          y += 8;
        });
      }
      
      pdf.setFontSize(9);
      pdf.setTextColor(128, 128, 128);
      
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Generated by ${agent?.name || 'AI Agent'} on ${new Date().toLocaleDateString()} | Page ${i} of ${pageCount}`, margin, pdf.internal.pageSize.getHeight() - 10);
      }
      
      pdf.save(`${(agent?.name || 'ai-agent').toLowerCase().replace(/\s+/g, '-')}-response.pdf`);
    } catch (error) {
      console.error('Error creating PDF:', error);
      toast.error('Failed to create PDF');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {agent?.name || 'AI Agent'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="bg-indigo-50 dark:bg-indigo-900/50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-indigo-800 dark:text-indigo-200 mb-2">About this Agent</h3>
            <p className="text-sm text-indigo-700 dark:text-indigo-300">
              {agent?.description || ''}
            </p>
            {agent?.system_prompt && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-indigo-800 dark:text-indigo-200 mb-1">System Prompt:</h4>
                <p className="text-sm text-indigo-700 dark:text-indigo-300">{agent?.system_prompt}</p>
              </div>
            )}
            {agent?.attachment_url && (
              <div className="mt-4">
                <div className="flex items-center">
                  <FileText className="h-4 w-4 text-indigo-600 dark:text-indigo-400 mr-2" />
                  <a
                    href={agent?.attachment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    {agent?.attachment_name || 'View attachment'}
                  </a>
                </div>
                {isProcessingDocument && (
                  <p className="mt-2 text-sm text-indigo-600 dark:text-indigo-400">
                    Processing document...
                  </p>
                )}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="w-full sm:w-1/2">
                <label htmlFor="model-select" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Model
                </label>
                <select 
                  id="model-select"
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                  <option value="gpt-4">GPT-4</option>
                  <option value="gpt-4-turbo">GPT-4 Turbo</option>
                  <option value="claude-3-opus">Claude 3 Opus</option>
                  <option value="claude-3-sonnet">Claude 3 Sonnet</option>
                  <option value="claude-3-haiku">Claude 3 Haiku</option>
                  <option value="gemini-pro">Gemini Pro</option>
                  <option value="gemini-ultra">Gemini Ultra</option>
                  <option value="o1">O1</option>
                  <option value="o3-mini-high">O3 Mini High</option>
                  <option value="gpt-4.1">GPT-4.1</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="user-input" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Enter your request
              </label>
              <textarea
                id="user-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="What would you like to know?"
                className="w-full h-24 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                disabled={loading}
              />
            </div>

            <div className="flex justify-end gap-4">
              {!loading && result && (
                <>
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </button>
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Text
                  </button>
                  <button
                    type="button"
                    onClick={downloadPDF}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Download PDF
                  </button>
                </>
              )}
              {result && !savedOutput?.saved && (
                <button
                  type="button"
                  onClick={handleSaveOutput}
                  disabled={savingOutput}
                  className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                    savingOutput ? 'opacity-75 cursor-not-allowed' : ''
                  }`}
                >
                  {savingOutput ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Output
                </button>
              )}
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                  (loading || !input.trim()) ? 'opacity-75 cursor-not-allowed' : ''
                }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-4 w-4 mr-2" />
                    Generate
                  </>
                )}
              </button>
            </div>
          </form>

          {result && (
            <div className="mt-8">
              <div
                className="prose prose-indigo dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: result }}
              />
            </div>
          )}

        </div>
      </div>
    </div>
  );
}